import React, { useMemo, useRef } from "react";
import { Calendar as CalendarIcon } from "lucide-react";

/**
 * DatePickerIT — input data localizzato italiano (gg/mm/aaaa).
 *
 * Implementazione: usa `<input type="date">` nativo (HTML5).
 * - Su iOS apre il picker nativo a rotella localizzato in italiano
 * - Su Android apre il calendar nativo Material
 * - Su desktop apre il picker nativo del browser
 *
 * Mostriamo sopra l'input un overlay "gg/mm/aaaa" o la data scelta in formato
 * italiano: l'input nativo (con `opacity:0`) cattura comunque il tap e apre
 * il picker. Così abbiamo il look custom + il comportamento nativo perfetto.
 *
 * Contratto: `value` e `onChange(iso)` lavorano sempre con stringa ISO YYYY-MM-DD.
 */
export default function DatePickerIT({
  value,
  onChange,
  maxDate,
  testid,
  placeholder = "gg/mm/aaaa",
}) {
  const inputRef = useRef(null);

  const display = useMemo(() => {
    if (!value) return "";
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!m) return "";
    return `${m[3]}/${m[2]}/${m[1]}`;
  }, [value]);

  const maxIso = useMemo(() => {
    if (!maxDate) return undefined;
    const y = maxDate.getFullYear();
    const m = String(maxDate.getMonth() + 1).padStart(2, "0");
    const d = String(maxDate.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [maxDate]);

  const openPicker = () => {
    const el = inputRef.current;
    if (!el) return;
    // showPicker() è supportato da iOS 17.4+, Chrome, Edge. Fallback: focus + click
    if (typeof el.showPicker === "function") {
      try {
        el.showPicker();
        return;
      } catch {
        /* fallback */
      }
    }
    el.focus();
    el.click();
  };

  return (
    <div
      className="relative w-full"
      onClick={openPicker}
      role="button"
      tabIndex={0}
    >
      {/* Visual layer (custom look) */}
      <div className="w-full flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-4 h-12 text-white/90 text-sm hover:border-white/20 transition-colors pointer-events-none">
        <span className="text-white/50">
          <CalendarIcon size={16} />
        </span>
        <span className={display ? "text-white" : "text-white/35"}>
          {display || placeholder}
        </span>
      </div>

      {/* Native input layer (invisible but tappable) */}
      <input
        ref={inputRef}
        type="date"
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        max={maxIso}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        // iOS: il picker nativo si apre solo se l'input non è readonly
        aria-label="Data di nascita"
      />
    </div>
  );
}
